import { useEffect, useState, useRef } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

const insightFeatures = [
  {
    id: 'dashboard',
    title: "Dashboard + insights",
    shortTitle: "Dashboard",
    description: "What your Genies did while you were away: people helped, issues resolved, hours saved.",
    src: '/home-page-new/Dashboard.png',
    alt: 'Help Genie dashboard showing people helped, issues resolved, and hours saved',
    path: '/dashboard',
  },
  {
    id: 'conversations',
    title: "Conversations + sentiment",
    shortTitle: "Conversations",
    description: "Every conversation across every channel: sentiment, satisfaction, and outcomes, at a glance.",
    src: '/home-page-new/Genie%20Inbox.png',
    alt: 'Help Genie inbox listing recent conversations with sentiment and satisfaction scores',
    path: '/inbox',
  },
  {
    id: 'knowledge',
    title: "Knowledge base",
    shortTitle: "Knowledge",
    description: "Every doc, FAQ, and file your Genies are trained on. One brain, organized in one place.",
    src: '/home-page-new/Genie%20Knowledge%20Base.png',
    alt: 'Help Genie knowledge base with folders and documents the genies are trained on',
    path: '/knowledge-base',
  },
  {
    id: 'genies-grid',
    title: "Your genies",
    shortTitle: "Genies",
    description: "Every genie you've spun up: status, channels, voice, and owner, in one place.",
    src: '/home-page-new/Genies%20Page.png',
    alt: 'Help Genie roster grid showing all of your genies',
    path: '/genies',
  },
];

export default function HomeInsights() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % insightFeatures.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered]);

  // Scroll active tab into view on mobile (horizontal only, no page scroll)
  useEffect(() => {
    if (tabsContainerRef.current) {
      const activeButton = tabsContainerRef.current.querySelector(`[data-index="${activeIndex}"]`) as HTMLElement;
      if (activeButton) {
        // Use scrollLeft instead of scrollIntoView to avoid page scroll
        const container = tabsContainerRef.current;
        const buttonLeft = activeButton.offsetLeft;
        const buttonWidth = activeButton.offsetWidth;
        const containerWidth = container.offsetWidth;
        const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeIndex]);

  return (
    <section id="insights-advantage" className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#222B45]">
            One application. <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">Every genie.</span> Every conversation.
          </h2>
          <p className="mt-3 text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            Watch what your Genies are doing the way you'd watch a team: conversations, knowledge, and insights, all in one place.
          </p>
          <p className="mt-4 text-sm sm:text-base text-gray-500">
            Explore more <a href="/help-genie-for-business-solutions" className="text-genie-primary hover:text-genie-secondary transition-colors">solutions</a>,{" "}
            <a href="/playbooks" className="text-genie-primary hover:text-genie-secondary transition-colors">playbooks</a>, and{" "}
            <a href="/pricing" className="text-genie-primary hover:text-genie-secondary transition-colors">pricing</a>.
          </p>
        </div>

        {/* Feature tabs - horizontal scroll on mobile, centered wrap on desktop */}
        <div
          ref={tabsContainerRef}
          className="flex md:flex-wrap md:justify-center gap-2 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory md:snap-none"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {insightFeatures.map((feature, index) => (
            <button
              key={feature.id}
              data-index={index}
              onClick={() => setActiveIndex(index)}
              className={cn(
                'relative px-4 py-2.5 sm:py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 snap-center touch-manipulation min-h-[44px]',
                index === activeIndex
                  ? 'bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
              )}
            >
              {/* Show short title on mobile, full title on larger screens */}
              <span className="sm:hidden">{feature.shortTitle}</span>
              <span className="hidden sm:inline">{feature.title}</span>
              {index === activeIndex && !isHovered && (
                <span className="absolute bottom-0 left-0 h-0.5 bg-white/50 animate-progress rounded-full" style={{ animationDuration: '4s' }} />
              )}
            </button>
          ))}
        </div>

        {/* Browser-framed screenshot display */}
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-[0_40px_80px_-24px_rgba(78,156,255,0.30)]">
            {/* Browser chrome bar — brand-tinted traffic lights + live URL */}
            <div className="flex items-center gap-2 border-b border-[#E5EAF0] bg-[#F9FAFC] px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-[#4E9CFF]" />
              <span className="h-3 w-3 rounded-full bg-[#FFBB33]" />
              <span className="h-3 w-3 rounded-full bg-[#FF4DB8]" />
              <div className="mx-auto hidden w-full max-w-xs items-center justify-center gap-1.5 rounded-full border border-[#E5EAF0] bg-white py-1.5 text-xs font-medium text-gray-400 sm:flex">
                <Lock className="h-3 w-3" />
                <span>helpgenie.ai{insightFeatures[activeIndex].path}</span>
              </div>
              {/* keeps the dots left-aligned when the URL pill is hidden on mobile */}
              <div className="w-[54px] sm:hidden" />
            </div>

            {/* Screen */}
            <div className="relative bg-white">
              {insightFeatures.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`transition-opacity duration-500 ${
                    index === activeIndex ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pointer-events-none'
                  }`}
                >
                  <img
                    src={feature.src}
                    alt={feature.alt}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    className="w-full h-auto"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Description below image */}
        <p className="mt-4 text-center text-gray-600 text-xs sm:text-sm lg:text-base px-2">
          {insightFeatures[activeIndex].description}
        </p>

        {/* Mobile swipe indicator */}
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {insightFeatures.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              aria-label={`Go to feature ${index + 1}`}
            >
              <span
                className={cn(
                  'w-2 h-2 rounded-full transition-all',
                  index === activeIndex ? 'bg-genie-primary scale-125' : 'bg-gray-300'
                )}
              />
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
